/**
 * EditWorkoutRoutine Component
 *
 * This component handles the edit tab for workout routines.
 *
 * Migration Notes:
 * - Original implementation used WorkoutRoutineContext directly
 * - New implementation uses React Hook Form + Zod with WorkoutGroupField components
 * - Legacy code has been removed as part of Requirements 7.3
 *
 * Requirements: 1.1, 1.3, 7.3 - React Hook Form + nested field arrays, legacy removal
 */

import * as React from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import styled from 'styled-components';

import {QueryGetExercises} from '../../../services';
import {WorkoutGroupField} from './WorkoutRoutineForm/_components/WorkoutGroupField/WorkoutGroupField';
import {
  workoutRoutineSchema,
  createDefaultWorkoutGroup,
  type WorkoutRoutineFormData,
} from './WorkoutRoutineForm/WorkoutRoutineForm.schema';
import {
  saveWorkoutRoutine,
  saveAllWorkoutGroups,
} from './WorkoutRoutineForm/WorkoutRoutineForm.service';
import {formatDate} from '../../../services/Date';

// Styled components
const WorkoutGroupsContainer = styled.div`
  margin-top: 20px;
`;

const AddWorkoutGroupButton = styled.div`
  background: #f5f5f5;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
    border-color: #999;
  }
`;

const SubmitButtonContainer = styled.div`
  margin-top: 20px;
  padding: 20px 0;
`;

/**
 * Props for EditWorkoutRoutine component
 */
interface EditWorkoutRoutineProps {
  workoutRoutine?: any;
  onWorkoutRoutineUpdate?: (data: any) => void;
}

/**
 * EditWorkoutRoutine Component using React Hook Form
 * Uses WorkoutGroupField components for nested form management
 */
function EditWorkoutRoutine({
  workoutRoutine,
  onWorkoutRoutineUpdate,
}: EditWorkoutRoutineProps): JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // State for exercise list
  const [exerciseListDefault, setExerciseListDefault] = React.useState<any[]>(
    [],
  );

  // Load exercises on mount
  React.useEffect(() => {
    QueryGetExercises()
      .then(({data}: {data: any}) => {
        const {list} = data;
        const exerciseTranslated = list.map((exercise: any) => ({
          label: exercise.name,
          value: exercise.id,
          animatedImage: exercise.animatedImage,
          image: exercise.image,
        }));
        setExerciseListDefault(exerciseTranslated);
      })
      .catch((err: any) => {
        console.log('error loading exercises:', err);
      });
  }, []);

  // Initialize form with React Hook Form + Zod
  const form = useForm<WorkoutRoutineFormData>({
    resolver: zodResolver(workoutRoutineSchema) as any,
    defaultValues: {
      id: workoutRoutine?.id || undefined,
      name: workoutRoutine?.name || '',
      startDate: workoutRoutine?.startDate || formatDate(new Date()),
      endDate: workoutRoutine?.endDate || formatDate(new Date()),
      cycleType: workoutRoutine?.cycleType || 'date',
      target: workoutRoutine?.target || 'programs',
      commentEnable: workoutRoutine?.commentEnable ?? true,
      programIds: workoutRoutine?.programIds || [],
      programsSelected: workoutRoutine?.programsSelected || [],
      usersTargetIds: workoutRoutine?.usersTargetIds || [],
      usersTarget: workoutRoutine?.usersTarget || [],
      weekDayIds: workoutRoutine?.weekDayIds || [],
      workoutGroups: workoutRoutine?.workoutGroups || [],
    },
    mode: 'onChange',
  });

  // Sync form values when workoutRoutine changes
  React.useEffect(() => {
    if (workoutRoutine) {
      form.reset({
        id: workoutRoutine.id || undefined,
        name: workoutRoutine.name || '',
        startDate: workoutRoutine.startDate || formatDate(new Date()),
        endDate: workoutRoutine.endDate || formatDate(new Date()),
        cycleType: workoutRoutine.cycleType || 'date',
        target: workoutRoutine.target || 'programs',
        commentEnable: workoutRoutine.commentEnable ?? true,
        programIds: workoutRoutine.programIds || [],
        programsSelected: workoutRoutine.programsSelected || [],
        usersTargetIds: workoutRoutine.usersTargetIds || [],
        usersTarget: workoutRoutine.usersTarget || [],
        weekDayIds: workoutRoutine.weekDayIds || [],
        workoutGroups: workoutRoutine.workoutGroups || [],
      });
    }
  }, [workoutRoutine?.id]);

  // Create initial workout group if none exist
  React.useEffect(() => {
    const workoutGroups = form.getValues('workoutGroups') || [];
    if (workoutGroups.length === 0) {
      const newGroup = createDefaultWorkoutGroup();
      form.setValue('workoutGroups', [newGroup]);
    }
  }, []);

  // Add workout group handler
  const handleAddWorkoutGroup = React.useCallback(() => {
    const currentGroups = form.getValues('workoutGroups') || [];
    const newGroup = createDefaultWorkoutGroup();
    form.setValue('workoutGroups', [...currentGroups, newGroup]);

    // Focus on the new group's name input
    setTimeout(() => {
      const newIndex = currentGroups.length;
      const element = document.getElementById(`workoutGroup-${newIndex}`);
      if (element) {
        element.focus();
      }
    }, 100);
  }, [form]);

  // Remove workout group handler
  const handleRemoveWorkoutGroup = React.useCallback(
    (index: number) => {
      const currentGroups = form.getValues('workoutGroups') || [];
      const group = currentGroups[index];

      if (group?.id) {
        // Mark for destruction if persisted
        const updatedGroups = [...currentGroups];
        updatedGroups[index] = {...group, _destroy: true};
        form.setValue('workoutGroups', updatedGroups);
      } else {
        // Remove if new
        const updatedGroups = currentGroups.filter((_, i) => i !== index);
        form.setValue('workoutGroups', updatedGroups);
      }
    },
    [form],
  );

  // Move workout group handler
  const handleMoveWorkoutGroup = React.useCallback(
    (from: number, to: number) => {
      const currentGroups = form.getValues('workoutGroups') || [];
      const updatedGroups = [...currentGroups];
      const [movedGroup] = updatedGroups.splice(from, 1);
      updatedGroups.splice(to, 0, movedGroup);
      form.setValue('workoutGroups', updatedGroups);
    },
    [form],
  );

  // Handle form submission
  const handleSubmit = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      const isValid = await form.trigger();
      if (!isValid) {
        return;
      }

      const formData = form.getValues();
      setLoading(true);

      try {
        // First save the routine configuration
        const routineResult = await saveWorkoutRoutine(formData, {
          showNotifications: true,
        });

        if (!routineResult.success) {
          setErrors(routineResult.errors || {});
          return;
        }

        // If we have workout groups and a routine ID, save them
        const routineId = routineResult.data?.id || formData.id;
        if (
          routineId &&
          formData.workoutGroups &&
          formData.workoutGroups.length > 0
        ) {
          const groupsResult = await saveAllWorkoutGroups(
            routineId,
            formData.workoutGroups,
            {showNotifications: true},
          );

          if (!groupsResult.success) {
            setErrors(groupsResult.errors || {});
            return;
          }

          // Update parent with saved data
          if (onWorkoutRoutineUpdate && groupsResult.data) {
            onWorkoutRoutineUpdate({
              ...workoutRoutine,
              ...routineResult.data,
              workoutGroups: groupsResult.data,
            });
          }
        } else {
          // Update parent with routine data only
          if (onWorkoutRoutineUpdate) {
            onWorkoutRoutineUpdate({
              ...workoutRoutine,
              ...routineResult.data,
            });
          }
        }

        setErrors({});
      } catch (error) {
        console.error('Error saving workout routine:', error);
        setErrors({general: 'Erro ao salvar rotina de treino'});
      } finally {
        setLoading(false);
      }
    },
    [form, workoutRoutine, onWorkoutRoutineUpdate],
  );

  // Get visible workout groups (not marked for destruction)
  const workoutGroups = form.watch('workoutGroups') || [];
  const visibleWorkoutGroups = workoutGroups.filter(
    (group) => !group?._destroy,
  );

  return (
    <FormProvider {...form}>
      <form className="form-workout-groups" onSubmit={handleSubmit}>
        <WorkoutGroupsContainer>
          {workoutGroups.map((group, index) => {
            // Skip destroyed groups
            if (group?._destroy) return null;

            const visibleIndex = visibleWorkoutGroups.findIndex(
              (g) =>
                (g.id && g.id === group.id) ||
                (g.tempId && g.tempId === group.tempId),
            );

            return (
              <WorkoutGroupField
                key={group.id || group.tempId || index}
                form={form as any}
                groupIndex={index}
                onRemove={() => handleRemoveWorkoutGroup(index)}
                onMoveUp={
                  visibleIndex > 0
                    ? () => handleMoveWorkoutGroup(index, index - 1)
                    : undefined
                }
                onMoveDown={
                  visibleIndex < visibleWorkoutGroups.length - 1
                    ? () => handleMoveWorkoutGroup(index, index + 1)
                    : undefined
                }
                isFirst={visibleIndex === 0}
                isLast={visibleIndex === visibleWorkoutGroups.length - 1}
                exerciseListDefault={exerciseListDefault}
              />
            );
          })}

          <AddWorkoutGroupButton onClick={handleAddWorkoutGroup}>
            <i className="fas fa-plus" style={{marginRight: '8px'}} />
            Adicionar novo treino
          </AddWorkoutGroupButton>
        </WorkoutGroupsContainer>

        <SubmitButtonContainer>
          <div className="row">
            <div className="col s12">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}>
                {loading ? 'Salvando...' : 'Publicar alterações'}
              </button>
            </div>
          </div>
        </SubmitButtonContainer>
      </form>
    </FormProvider>
  );
}

export default EditWorkoutRoutine;
